/**
 * MetallicHeroBackdrop — dekorativ "flytande metall"-bakgrund för WelcomeHero.
 *
 * Lager, bakifrån och fram:
 *   1) nästan svart bas — djup grafit mot ren svärta
 *   2) subtila krom-band — SVG-kurvor med dämpade silvergradienter +
 *      gaussisk blur som ger en mjuk, nästan omärklig metallkänsla
 *   3) borstad metall-grain — hårfina diagonala linjer, låg opacitet
 *
 * Helt dekorativt: aria-hidden + pointer-events-none. Ren gråskala i linje
 * med appens monokroma Liquid Glass-designspråk — inga färgtoner.
 */
export function MetallicHeroBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 select-none">
      {/* 1) Basgradient — nästan svart, djup grafit mot ren svärta */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(150deg, #0c0e12 0%, #08090d 40%, #040507 78%, #010102 100%)',
        }}
      />

      {/* 2) Subtila krom-band — "none" så hela kompositionen fyller ytan
          oavsett hjältens bildförhållande (mobil ~1.4:1, desktop ~7:1) */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 800 500"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="hero-metal-ribbon" x1="0" y1="0" x2="1" y2="0.35">
            <stop offset="0" stopColor="#c7cdd6" stopOpacity="0" />
            <stop offset="0.32" stopColor="#b9c0c9" stopOpacity="0.32" />
            <stop offset="0.5" stopColor="#5c636d" stopOpacity="0.18" />
            <stop offset="0.68" stopColor="#d0d6de" stopOpacity="0.26" />
            <stop offset="1" stopColor="#7b838e" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="hero-metal-shadow" x1="0" y1="0" x2="1" y2="0.2">
            <stop offset="0" stopColor="#000000" stopOpacity="0" />
            <stop offset="0.5" stopColor="#000000" stopOpacity="0.6" />
            <stop offset="1" stopColor="#000000" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="hero-metal-pool" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#e8ecf2" stopOpacity="0.07" />
            <stop offset="1" stopColor="#e8ecf2" stopOpacity="0" />
          </radialGradient>
          <filter id="hero-metal-soft" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="18" />
          </filter>
          <filter id="hero-metal-crisp" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3.5" />
          </filter>
        </defs>

        {/* Mjuka ljuspölar — mycket dämpade */}
        <ellipse cx="600" cy="130" rx="260" ry="150" fill="url(#hero-metal-pool)" />
        <ellipse cx="150" cy="420" rx="240" ry="140" fill="url(#hero-metal-pool)" opacity="0.6" />

        {/* Brett silverband genom övre halvan */}
        <path
          d="M-80 150 C140 40, 320 240, 520 170 C660 120, 760 60, 900 130"
          fill="none"
          stroke="url(#hero-metal-ribbon)"
          strokeWidth="80"
          filter="url(#hero-metal-soft)"
          opacity="0.4"
        />
        {/* Mörkt band under — ger flytande djup mellan högdagrarna */}
        <path
          d="M-80 240 C180 160, 380 320, 620 240 C740 200, 820 220, 900 210"
          fill="none"
          stroke="url(#hero-metal-shadow)"
          strokeWidth="70"
          filter="url(#hero-metal-soft)"
          opacity="0.5"
        />
        {/* Smalare silverband i nedre halvan */}
        <path
          d="M-60 330 C200 380, 420 260, 640 330 C740 360, 820 330, 900 300"
          fill="none"
          stroke="url(#hero-metal-ribbon)"
          strokeWidth="46"
          filter="url(#hero-metal-soft)"
          opacity="0.22"
        />
        {/* Skarp krom-kant längs det övre bandet */}
        <path
          d="M-80 140 C140 30, 320 230, 520 160 C660 110, 760 50, 900 120"
          fill="none"
          stroke="url(#hero-metal-ribbon)"
          strokeWidth="2"
          filter="url(#hero-metal-crisp)"
          opacity="0.45"
        />
      </svg>

      {/* 3) Borstad metall-grain — låg opacitet */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'repeating-linear-gradient(112deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 4px)',
        }}
      />
    </div>
  );
}
